import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Eye, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { toast } from "sonner";
import {
  getAllApplications,
  updateApplicationStatus,
} from "../../services/Application";

interface Application {
  id: number;
  name: string;
  email: string;
  mobileNumber?: string | null;
  yearsOfExperience: string;
  education: string | null;
  expectedPayPerDay: string | null;
  expectedPayPerMonth: string | null;
  numbersOfProjectsWorked: string | null;
  resumeUrl: string;
  coverLetter: string;
  jobId: number;
  jobTitle: string;
  status: string;
  appliedDate: string;
}

export function ViewApplications() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>("all");

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const data = await getAllApplications();
      setApplications(data);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-blue-100 text-blue-800";
      case "REVIEWED":
        return "bg-yellow-100 text-yellow-800";
      case "ACCEPTED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get unique job titles for filter
  const uniqueJobTitles = Array.from(
    new Set(applications.map((app) => app.jobTitle))
  ).sort();

  // Filter applications based on selected job title
  const filteredApplications =
    selectedJobTitle === "all"
      ? applications
      : applications.filter((app) => app.jobTitle === selectedJobTitle);

  const handleStatusUpdate = async (id: number, newStatus: string) => {
    setUpdating(true);
    try {
      await updateApplicationStatus(id, newStatus);
      toast.success("Status updated successfully");
      fetchApplications();
    } catch (error: any) {
      toast.error(error.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <p className="text-center text-gray-500 py-8">
            Loading applications...
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Job Applications</CardTitle>
          <Badge variant="secondary">
            {filteredApplications.length} Application{filteredApplications.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Filter Section */}
        <div className="mt-4 flex items-center gap-3">
          <label htmlFor="jobTitleFilter" className="text-sm font-medium">
            Filter by Job Title:
          </label>
          <select
            id="jobTitleFilter"
            value={selectedJobTitle}
            onChange={(e) => setSelectedJobTitle(e.target.value)}
            className="text-sm border rounded px-3 py-1.5 bg-white cursor-pointer"
          >
            <option value="all">All Jobs ({applications.length})</option>
            {uniqueJobTitles.map((title) => {
              const count = applications.filter(
                (app) => app.jobTitle === title
              ).length;
              return (
                <option key={title} value={title}>
                  {title} ({count})
                </option>
              );
            })}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">
              {selectedJobTitle === "all"
                ? "No applications received yet."
                : `No applications found for "${selectedJobTitle}".`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Job Title</TableHead>
                  <TableHead>Applied Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>{app.name}</TableCell>
                    <TableCell>{app.email}</TableCell>
                    <TableCell>{app.yearsOfExperience}</TableCell>
                    <TableCell>{app.jobTitle}</TableCell>
                    <TableCell>
                      {new Date(app.appliedDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <select
                        value={app.status}
                        disabled={updating}
                        onChange={(e) =>
                          handleStatusUpdate(app.id, e.target.value)
                        }
                        className={`text-sm border rounded px-2 py-1 cursor-pointer ${getStatusColor(
                          app.status
                        )}`}
                      >
                        <option value="PENDING">Pending</option>
                        <option value="REVIEWED">Reviewed</option>
                        <option value="ACCEPTED">Accepted</option>
                        <option value="REJECTED">Rejected</option>
                      </select>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="cursor-pointer"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Application Details</DialogTitle>
                          </DialogHeader>

                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="mb-1 font-medium">
                                  Applicant Name
                                </h4>
                                <p className="text-gray-600">{app.name}</p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">Email</h4>
                                <p className="text-gray-600">{app.email}</p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">Phone</h4>
                                <p className="text-gray-600">
                                  {app.mobileNumber || "N/A"}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">Experience</h4>
                                <p className="text-gray-600">
                                  {app.yearsOfExperience}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">
                                  Education
                                </h4>
                                <p className="text-gray-600">
                                  {app.education || "N/A"}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">
                                  Expected Pay / Day
                                </h4>
                                <p className="text-gray-600">
                                  ₹{app.expectedPayPerDay || "N/A"}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">
                                  Expected Pay / Month
                                </h4>
                                <p className="text-gray-600">
                                  ₹{app.expectedPayPerMonth || "N/A"}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">
                                  Projects Worked
                                </h4>
                                <p className="text-gray-600">
                                  {app.numbersOfProjectsWorked || "N/A"}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">
                                  Applied Date
                                </h4>
                                <p className="text-gray-600">
                                  {new Date(app.appliedDate).toLocaleString()}
                                </p>
                              </div>

                              <div>
                                <h4 className="mb-1 font-medium">Status</h4>
                                <select
                                  value={app.status}
                                  onChange={(e) =>
                                    handleStatusUpdate(app.id, e.target.value)
                                  }
                                  className={`text-sm border rounded px-2 py-1 cursor-pointer ${getStatusColor(
                                    app.status
                                  )}`}
                                >
                                  <option value="PENDING">Pending</option>
                                  <option value="REVIEWED">Reviewed</option>
                                  <option value="ACCEPTED">Accepted</option>
                                  <option value="REJECTED">Rejected</option>
                                </select>
                              </div>
                            </div>

                            {/* <div>
                              <h4 className="mb-2 font-medium">
                                Cover Letter
                              </h4>
                              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg whitespace-pre-line">
                                {app.coverLetter}
                              </p>
                            </div> */}

                            {app.resumeUrl && (
                              <div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    window.open(app.resumeUrl, "_blank")
                                  }
                                  className="cursor-pointer"
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  View Resume
                                </Button>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}